import { Component, computed, effect, inject, Inject, signal, Signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorParser } from '@app/classes';
import { FeatureFlag } from '@app/enums';

import { BadgeOptions } from '@app/interfaces';
import { RL1Summary } from '@app/models/payroll/rl1-summary.model';
import { T4Summary } from '@app/models/payroll/t4-summary.model';
import { TaxForm } from '@app/models/payroll/tax-form.abstract.model';
import { TaxReturnSummary } from '@app/models/payroll/tax-return-summary.abstract.model';
import { TaxReturn } from '@app/models/payroll/tax-return.model';
import { TaxFormConsentDialog } from '@app/modules/payroll/components/tax-returns/tax-form-consent-dialog/tax-form-consent-dialog.component';
import { PayrollResolver } from '@app/modules/payroll/payroll.resolver';
import { TaxReturnService } from '@app/modules/payroll/services';
import { PayrollModuleMenuService } from '@app/modules/payroll/services/payroll-module-menu.service';
import { AuthService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { Employee } from '@models/employee/employee.model';
import { from } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

@Component({ template: `` })
export class ReturnShowView {
    @ViewChild(TaxFormConsentDialog) emailConfirmationDialog!: TaxFormConsentDialog;
    payrollModuleMenuService: PayrollModuleMenuService = inject(PayrollModuleMenuService);
    form: TaxForm = this.route.snapshot.data.models.get(this.model);
    headerBadgeOptions: BadgeOptions = this.form.stateBadge;

    /**
     * Whether the entire page is loading, can be triggered by setting isLoading, but also relies on underlying data being set
     */
    get isLoading(): boolean {
        return this._isLoading || !this.employee || !this.backRoute;
    }

    set isLoading(isLoading: boolean) {
        this._isLoading = isLoading;
    }

    // Since this class is meant to be extended, we inject all of the common services to avoid having to re-define them in the super classes
    protected notify: NotifyService = inject(NotifyService);
    protected auth: AuthService = inject(AuthService);
    protected featureService: FeatureService = inject(FeatureService);
    protected resolver: PayrollResolver = inject(PayrollResolver);
    protected router: Router = inject(Router);
    protected taxReturnService: TaxReturnService = inject(TaxReturnService);
    protected canCreateAmendment = computed(() =>
        this.hasQcPayrollEnabled() ? this.taxReturn()?.isComplete : !this.form.isOpen
    );
    protected backRoute = signal<string | undefined>(undefined);
    protected hasQcPayrollEnabled = toSignal(from(this.featureService.has(FeatureFlag.qcPayroll)));
    /**
     * The employee associated with the return slip
     */
    protected employee: Signal<Employee | undefined> = toSignal(
        from(
            Employee.param('company', this.auth.company.id)
                .where('prEmployeeId', this.form.employee.id)
                .whereIn('status', ['active', 'terminated', 'on leave'])
                .get()
        ).pipe(map(([employees]) => (employees.length ? employees[0] : undefined))) // There should only ever be 1 employee returned, but we need to call get() because we're filtering by ID
    );

    private summary = signal<TaxReturnSummary | undefined>(undefined);
    private taxReturn = signal<TaxReturn | undefined>(undefined);
    private _isLoading = true;

    /**
     * Loads the summary model.
     * Only necessary when the RL-1 flag is enabled since we need the summary in order to load the parent TaxReturn model
     */
    private loadSummary = effect(
        () => {
            if (!this.hasQcPayrollEnabled()) {
                this.backRoute.set(`/payroll/records/tax-returns/${this.form.year}/${this.form.summaryId}`);
                return;
            }
            const summaryModel = this.model === 'rl1' ? RL1Summary : T4Summary;
            summaryModel.param('company', this.auth.company.id).find(this.form.summaryId).then(this.summary.set);
        },
        { allowSignalWrites: true }
    );

    /**
     * Loads the TaxReturn model.
     * Only necessary when the RL-1 flag is enabled in order to build the backRoute and determine if an amendment can be created (can't add amendment to an open TaxReturn from this view)
     */
    private loadReturn = effect(
        () => {
            const summary = this.summary();
            if (!summary?.taxReturnId) {
                return;
            }
            this.backRoute.set(`/payroll/records/tax-returns/${this.form.year}/${summary.taxReturnId}`);

            if (summary.state === 'open') {
                return; // No need to get tax return we know the summary is not in a state where we can create an amendment
            }

            TaxReturn.find(summary.taxReturnId).then(this.taxReturn.set);
        },
        { allowSignalWrites: true }
    );

    constructor(
        protected route: ActivatedRoute,
        @Inject('model') private model: 't4' | 'rl1' // Provided by the super class. This indicates which model to try and pull from the route via the resolver
    ) {}

    handleEditEmployee(): void {
        this.router.navigate(['edit-employee'], { relativeTo: this.route });
    }

    handleEditBoxes(): void {
        this.router.navigate(['edit-details'], { relativeTo: this.route });
    }

    /**
     * Called when a user reverts a return to its original state
     */
    handleRevert(): void {
        this.isLoading = true;
        this.taxReturnService
            .revertToOriginalState(this.form)
            .pipe(
                tap(() =>
                    this.notify.success(
                        `payroll.taxForms.${this.model.toUpperCase()}.employee.revertToOriginal.success`
                    )
                ),
                switchMap(() => from(this.resolver.resolve(this.route.snapshot, this.router.routerState.snapshot)))
            )
            .subscribe({
                next: (models) => {
                    this.form = models.get(this.model) as TaxForm;
                    this.isLoading = false;
                },
                error: (error) => {
                    this.notify.error(ErrorParser.parse(error as Record<string, unknown>));
                    this.isLoading = false;
                },
            });
    }

    async handleDownload(taxForm: TaxForm): Promise<void> {
        this.isLoading = true;
        try {
            await this.taxReturnService.downloadTaxForm(taxForm);
        } catch (e) {
            this.notify.error(e);
        }
        this.isLoading = false;
    }

    async handlePreview(taxForm: TaxForm): Promise<void> {
        this.isLoading = true;
        try {
            await this.taxReturnService.downloadTaxFormPreview(taxForm);
        } catch (e) {
            this.notify.error(e);
        }
        this.isLoading = false;
    }

    async handleSend(taxForm: TaxForm): Promise<void> {
        const confirmed = await this.emailConfirmationDialog?.openDialog();

        if (!confirmed) return;

        this.isLoading = true;
        try {
            await this.taxReturnService.sendEmployeeEmail(taxForm);
            this.notify.success(`payroll.taxForms.${this.model.toUpperCase()}.employee.emailSent`);
        } catch (e) {
            this.notify.error(e);
        }
        this.isLoading = false;
    }

    async handleAddAmendment(taxForm: TaxForm): Promise<void> {
        this.isLoading = true;
        try {
            const amendment = await this.taxReturnService.addAmendment(taxForm);
            this.router.navigate(['../', amendment.id.toString()], { relativeTo: this.route });
        } catch (e) {
            this.notify.error(e);
        }
        this.isLoading = false;
    }

    async handleDelete(taxForm: TaxForm): Promise<void> {
        try {
            await this.taxReturnService.deleteTaxForm(taxForm);
            this.router.navigate([this.backRoute()]);
        } catch (e) {
            this.notify.error(e);
            this.isLoading = false;
        }
    }
}
